import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Cache ads per position for this session so we don't over-fetch
const adCache = {};

/**
 * AdBlock — fetches and renders one or more ad blocks for a given position.
 *
 * Usage:
 *   <AdBlock position="header" />
 *   <AdBlock position="home-middle" className="my-4" />
 *   <AdBlock position="jobs-sidebar" showEmpty />   ← show placeholder if no ads
 *
 * Ad types supported by admin:
 *   - html    : arbitrary HTML/embed/script (rendered via dangerouslySetInnerHTML inside iframe-style sandbox)
 *   - adsense : Google AdSense snippet (injected as raw HTML)
 *   - image   : image with optional link
 *   - text    : simple text/banner with heading, body and CTA link
 */
export default function AdBlock({ position, className = '', showEmpty = false }) {
  const [ads, setAds] = useState(null); // null = loading, [] = no ads
  const containerRef = useRef(null);

  useEffect(() => {
    if (!position) return;

    if (adCache[position]) {
      setAds(adCache[position]);
      return;
    }

    axios
      .get(`${API_URL}/api/ads?position=${encodeURIComponent(position)}`)
      .then((res) => {
        const raw = res.data;
        const normalized = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.results)
          ? raw.results
          : [];
        adCache[position] = normalized;
        setAds(normalized);
      })
      .catch(() => {
        adCache[position] = [];
        setAds([]);
      });
  }, [position]);

  // Execute scripts inside injected HTML (needed for AdSense & custom scripts)
  useEffect(() => {
    if (!containerRef.current) return;
    const scripts = containerRef.current.querySelectorAll('script');
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) =>
        newScript.setAttribute(attr.name, attr.value)
      );
      newScript.textContent = oldScript.textContent;
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }, [ads]);

  const normalizedAds = Array.isArray(ads) ? ads : [];

  // No ads and not asked to show empty state → render nothing
  if (normalizedAds.length === 0 && !showEmpty) return null;

  // No ads but showEmpty is set → render a subtle placeholder zone so the space is visible
  if (normalizedAds.length === 0 && showEmpty) {
    return (
      <div
        className={`ad-block-zone w-full ${className}`}
        data-ad-position={position}
      >
        <div
          style={{
            width: '100%',
            minHeight: '90px',
            background: 'linear-gradient(135deg,#f3f4f6,#e5e7eb)',
            border: '2px dashed #d1d5db',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            borderRadius: '6px',
            fontFamily: 'sans-serif',
            color: '#9ca3af',
            padding: '12px',
            boxSizing: 'border-box',
          }}
        >
          <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase' }}>
            Advertisement
          </span>
          <span style={{ fontSize: '13px', fontWeight: 600, marginTop: '4px' }}>
            Ad space — {position}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`ad-block-zone w-full ${className}`} data-ad-position={position}>
      {normalizedAds.map((ad) => (
        <SingleAd key={ad.id} ad={ad} />
      ))}
    </div>
  );
}

function SingleAd({ ad }) {
  if (!ad.is_active) return null;

  const wrapper = (content) => (
    <div
      className="ad-block relative overflow-hidden"
      data-ad-id={ad.id}
      data-ad-type={ad.ad_type}
    >
      {content}
    </div>
  );

  switch (ad.ad_type) {
    case 'html':
    case 'adsense':
      // Raw HTML/AdSense — render directly; scripts are executed by the parent effect
      return wrapper(
        <div
          dangerouslySetInnerHTML={{ __html: ad.content }}
          className="w-full"
        />
      );

    case 'image':
      return wrapper(
        ad.link_url ? (
          <a href={ad.link_url} target="_blank" rel="noopener noreferrer" className="block">
            <img
              src={ad.content}
              alt={ad.name || 'Advertisement'}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </a>
        ) : (
          <img
            src={ad.content}
            alt={ad.name || 'Advertisement'}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        )
      );

    case 'text':
      // Structured text banner: content is JSON string { heading, body, cta_label, bg_color, text_color }
      let data = {};
      try {
        data = typeof ad.content === 'string' ? JSON.parse(ad.content) : ad.content;
      } catch {
        data = { heading: ad.name, body: ad.content };
      }
      return wrapper(
        <div
          className="w-full py-3 px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm"
          style={{
            backgroundColor: data.bg_color || '#003366',
            color: data.text_color || '#ffffff',
          }}
        >
          <div>
            {data.heading && <span className="font-semibold mr-3">{data.heading}</span>}
            {data.body && <span className="opacity-80">{data.body}</span>}
          </div>
          {data.cta_label && ad.link_url && (
            <a
              href={ad.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 px-4 py-1.5 rounded-full text-xs font-semibold border border-white/40 hover:bg-white/10 transition-colors"
              style={{ color: data.text_color || '#ffffff' }}
            >
              {data.cta_label}
            </a>
          )}
        </div>
      );

    default:
      return null;
  }
}
